'use client';

import { runtimeConfig } from '@/lib';
import axios, { AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocale } from 'next-intl';
import { getAcceptLanguageHeader } from '@/config/i18n/locales';
import type { Locale } from '@/config/i18n/locales';
import { signOut, useSession } from 'next-auth/react';
import { useAuth } from '@/contexts';

let signOutOn401InFlight = false;

export const useAxiosInterceptors = () => {
  const { data: session } = useSession();
  const { getAccessToken } = useAuth();
  const locale = useLocale() as Locale;
  const localeRef = useRef(locale);
  const getAccessTokenRef = useRef(getAccessToken);

  useEffect(() => {
    localeRef.current = locale;
    getAccessTokenRef.current = getAccessToken;
  }, [locale, getAccessToken, session]);

  useLayoutEffect(() => {
    axios.defaults.baseURL = runtimeConfig.API_URL ?? process.env.NEXT_PUBLIC_API_URL;

    const requestInterceptor = (config: InternalAxiosRequestConfig) => {
      if (!config.baseURL && !axios.defaults.baseURL) {
        config.baseURL = runtimeConfig.API_URL ?? process.env.NEXT_PUBLIC_API_URL;
      }
      const isAuthRequest = config.url?.includes('/realms/');
      const token = getAccessTokenRef.current();
      if (token && !isAuthRequest) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
      config.headers.set('Accept-Language', getAcceptLanguageHeader(localeRef.current));
      return { ...config };
    };

    const responseErrorInterceptor = (error: unknown) => {
      const axiosError = error as { response?: { status: number } };
      if (axiosError?.response?.status !== 401) {
        return Promise.reject(error);
      }
      // A rejected token means the app session is dead. Drop it and stay on
      // this page. A guest request has no token, so it must not sign out again.
      if (getAccessTokenRef.current() && !signOutOn401InFlight) {
        signOutOn401InFlight = true;
        getAccessTokenRef.current = () => undefined;
        void signOut({ redirect: false }).finally(() => {
          signOutOn401InFlight = false;
        });
      }
      return Promise.reject(error);
    };

    const { request, response } = axios.interceptors;
    const reqInterceptor = request.use(requestInterceptor, (error) => Promise.reject(error));
    const resInterceptor = response.use((res: AxiosResponse) => res, responseErrorInterceptor);

    return () => {
      request.eject(reqInterceptor);
      response.eject(resInterceptor);
    };
  }, []);
};
